import React from 'react';
import { Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { BusinessAccountInfo } from '@/features/auth/types/auth.types';
import './ClientEntitySelector.css';

export interface ClientEntitySelectorProps {
  clients: BusinessAccountInfo[];
  selectedClientId: string | null;
  onClientChange: (clientId: string) => void;
  showClientSelector: boolean;
  className?: string;
}

/**
 * ClientEntitySelector
 *
 * Searchable Select for switching clients + a read-only display for the current entity.
 *
 * - **Client selector**: Interactive, searchable. Visible only for internal profiles
 *   with multi-client access. Selecting a different client triggers `change-account`.
 * - **Entity display**: Always **disabled/read-only**. Shows the platforms linked to
 *   the currently active `businessAccount`. Informative only — the entity is
 *   auto-selected from the first available platform after a client switch.
 */
export const ClientEntitySelector: React.FC<ClientEntitySelectorProps> = ({
  clients,
  selectedClientId,
  onClientChange,
  showClientSelector,
  className = '',
}) => {
  const { t } = useTranslation();

  const rootClass = ['client-entity-selector', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} data-testid="client-entity-selector">
      {showClientSelector && (
        <div className="client-entity-selector__field">
          <Typography.Text
            type="secondary"
            className="client-entity-selector__label"
          >
            {t('Change client')}
          </Typography.Text>
          <Select
            showSearch
            value={selectedClientId ?? undefined}
            placeholder={t('Select a client')}
            onChange={onClientChange}
            optionFilterProp="label"
            className="client-entity-selector__select client-entity-selector--client"
            aria-label={t('Change client')}
            notFoundContent={t('No clients available')}
            data-testid="client-select"
            options={clients.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
          />
        </div>
      )}
    </div>
  );
};

export default ClientEntitySelector;
